import _ from 'lodash';
import moment from 'moment';
/** import third-party libraries in the section above, keep the ascending order */

import config from '../../../../config';
/** import classes and others in the section above, keep the ascending order */

const DATE_FORMAT = 'MMMM Do, YYYY @ h:ma';

class Flags {
    constructor(initialFlags) {
        this.changeHandlers = [];
        this.flags = initialFlags;
        this.enabledFlags = [];
    }

    getFlags() {
        return this.flags;
    }

    getEnabledFlagKeys() {
        if (this.enabledFlags.length > 0) {
            return this.enabledFlags;
        }

        for (const key in this.flags) {
            if (this.isEnabled(key)) {
                this.enabledFlags.push(key);
            }
        }

        return this.enabledFlags;
    }

    isEnabled(key) {
        const flag = this._findFlag(key);
        if (!flag) {
            return false;
        }

        return flag.enabled;
    }

    isDisabled(key) {
        return this._findFlag(key).enabled === false;
    }

    disable(key) {
        const flag = this._findFlag(key);
        flag.enabled = false;

        this._persistFlags();
        this.emitChange();
    }

    enable(key) {
        const flag = this._findFlag(key);
        flag.enabled = true;

        this._persistFlags();
        this.emitChange();
    }

    reset() {
        // clear cache
        localStorage.setItem('LOCAL_FLAGS', JSON.stringify({}));

        // reset flags
        this.populate(this.initialFlags);

        this._persistFlags();
        this.emitChange();
    }

    toggleFlag(key) {
        const flag = this._findFlag(key);

        if (flag.enabled) {
            this.disable(key);
        } else {
            this.enable(key);
        }
    }

    emitChange() {
        _.each(this.changeHandlers, fn => fn(this));
    }

    onChange(fn) {
        this.changeHandlers.push(fn);
    }

    offChange(fn) {
        this.changeHandlers = this.changeHandlers.filter(handler => handler !== fn);
    }

    canManageFlags() {
        return _.includes(
            ['development', 'integration', 'release', 'cloud-dev', 'staging'],
            config.ENV_NAME
        );
    }

    _findFlag(key) {
        const flag = this.flags[key];
        if (!flag) {
            throw new Error(`Flag ${key} not found. Clean up flag reference or create the flag`);
        }

        return flag;
    }

    populate(flags) {
        let STORAGE_FLAGS = {};
        this.initialFlags = flags;

        try {
            if (config.ENV_NAME === 'development') {
                STORAGE_FLAGS = JSON.parse(localStorage.getItem('LOCAL_FLAGS') || '{}');
            }
        } catch (err) {
            STORAGE_FLAGS = {};
        }

        // Merge localStorage flags with most recent flag
        const allFlags = {};
        _.each(flags, flag => {
            const flagToUse = STORAGE_FLAGS[flag.key] || flag;
            const enabled = this._checkIfFlagIsEnabledConsideringEnvironments(flagToUse);
            allFlags[flagToUse.key] = {
                // REMOVE environments from the flag, as it was already used to define the enabled state
                ..._.omit(flagToUse, 'environments'),
                enabled,
            };
        });

        this.flags = allFlags;
    }

    _persistFlags() {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('LOCAL_FLAGS', JSON.stringify(this.flags));
            this._resetEnabledFlags();
        }
    }

    _resetEnabledFlags() {
        this.enabledFlags = [];
    }

    getStaleFlags() {
        return _(this.flags)
            .map(flag => flag)
            .filter(flag => flag.released)
            .filter(flag => moment().diff(moment(flag.released, DATE_FORMAT), 'months') >= 1)
            .groupBy('author')
            .value();
    }

    /**
     * Checks if a flag is enabled considering its environment restrictions.
     *
     * @param {Object} flag - The flag object to check
     * @param {boolean} flag.enabled - Whether the flag is enabled
     * @param {string[]} [flag.environments] - Array of environment names where the flag should be active.
     *                                          Possible values: "production", "demo", "development",
     *                                          "cloud-dev", "spain", "spain-staging"
     * @returns {boolean} True if the flag is enabled and (if environments are specified) the current
     *                    environment is in the allowed list; false otherwise
     * @private
     */
    _checkIfFlagIsEnabledConsideringEnvironments(flag) {
        const flagEnvironments = Array.isArray(flag.environments) ? flag.environments : [];

        if (flagEnvironments.length === 0) {
            return flag.enabled;
        }

        return flag.enabled && flagEnvironments.includes(config.ENV_NAME);
    }
}

const flagService = new Flags({});

export default flagService;
